import { z } from "zod";
export const roleSchema = z.enum(["USER", "SUPPORT", "ADMIN"]);
export const userStatusSchema = z.enum(["ACTIVE", "SUSPENDED", "DELETED"]);
export const subscriptionStatusSchema = z.enum([
    "PENDING",
    "ACTIVE",
    "PAST_DUE",
    "SUSPENDED",
    "EXPIRED",
    "CANCELLED",
]);
export const subscriptionSourceSchema = z.enum(["MANUAL", "PAYMENT", "TRIAL"]);
export const nodeStatusSchema = z.enum(["HEALTHY", "DEGRADED", "DOWN", "MAINTENANCE"]);
export const clientSchema = z.enum(["INCY", "HAPP"]);
export const connectionModeSchema = z.enum(["SMART_AUTO"]);
export const platformSchema = z.enum(["IOS", "ANDROID", "WINDOWS", "MACOS", "LINUX", "OTHER"]);
export const paymentMethodSchema = z.enum(["SBP", "CRYPTO"]);
export const renewalModeSchema = z.enum(["ONE_TIME", "RECURRING_NOW", "REMIND_LATER"]);
export const checkoutSchema = z.object({
    planId: z.uuid(),
    renewalMode: renewalModeSchema.default("ONE_TIME"),
    paymentMethod: paymentMethodSchema.optional().default("SBP"),
    receiptEmail: z.union([z.literal(""), z.email().max(254)]).optional().default(""),
    useWallet: z.boolean().optional().default(false),
    extraDeviceSlots: z.number().int().min(0).max(30).optional().default(0),
    idempotencyKey: z.uuid(),
});
export const checkoutResponseSchema = z.discriminatedUnion("flow", [
    z.object({
        flow: z.literal("ONE_TIME"),
        paymentId: z.uuid(),
        confirmationUrl: z.url(),
        status: z.string().min(1),
        idempotentReplay: z.boolean().optional(),
    }),
    z.object({
        flow: z.literal("RECURRING"),
        agreementId: z.uuid(),
        confirmationUrl: z.url(),
        status: z.string().min(1),
        idempotentReplay: z.boolean().optional(),
    }),
]);
export const botActionSchema = z.enum([
    "CONNECT",
    "TRIAL",
    "SUBSCRIPTION",
    "PLANS",
    "DEVICES",
    "WALLET",
    "REFERRAL",
    "SUPPORT",
    "LOGIN",
]);
export const botInlineContextSchema = z.enum([
    "WELCOME",
    "CHANNEL_GATE",
    "PROFILE_ACTIVE",
    "PROFILE_EMPTY",
    "TRIAL_ALREADY_USED",
    "TRIAL_READY",
    "TRIAL_OFFER",
    "LOGIN",
    "CONNECT",
    "PLANS",
    "DEVICES",
    "WALLET",
    "REFERRAL",
    "SUPPORT",
]);
export const botReplyButtonSchema = z.object({
    id: z.string().regex(/^[a-z0-9_-]{2,40}$/),
    action: botActionSchema,
    label: z.string().trim().min(1).max(64),
    command: z.string().regex(/^[a-z][a-z0-9_]{1,31}$/),
    commandDescription: z.string().trim().min(1).max(256),
    row: z.number().int().min(0).max(20),
    enabled: z.boolean(),
    showInMenu: z.boolean(),
});
export const botInlineButtonSchema = z.object({
    id: z.string().regex(/^[a-z0-9_-]{2,60}$/),
    context: botInlineContextSchema,
    label: z.string().trim().min(1).max(64),
    type: z.enum(["WEB_APP", "URL", "CALLBACK"]),
    target: z.string().trim().max(512),
    row: z.number().int().min(0).max(20),
    enabled: z.boolean(),
}).superRefine((button, ctx) => {
    if (button.type === "CALLBACK" && button.target !== "trial_channel_check") {
        ctx.addIssue({ code: "custom", path: ["target"], message: "Unsupported callback target" });
    }
    if (button.type === "URL" && !["$support", "$channel", "$referralShare"].includes(button.target)) {
        try {
            const url = new URL(button.target);
            if (url.protocol !== "https:")
                throw new Error("HTTPS required");
        }
        catch {
            ctx.addIssue({ code: "custom", path: ["target"], message: "URL target must be HTTPS or a supported variable" });
        }
    }
    if (button.type === "WEB_APP" && !/^[a-z0-9_-]{0,48}$/.test(button.target)) {
        ctx.addIssue({ code: "custom", path: ["target"], message: "Web App target must be a cabinet screen name" });
    }
});
export const botMessagesSchema = z.object({
    welcome: z.string().trim().min(1).max(3_500),
    referralAccepted: z.string().trim().min(1).max(1_000),
    openCabinet: z.string().trim().min(1).max(1_000),
    channelNotConfigured: z.string().trim().min(1).max(2_000),
    channelUnavailable: z.string().trim().min(1).max(2_000),
    channelRequired: z.string().trim().min(1).max(2_000),
    channelConfirmed: z.string().trim().min(1).max(2_000),
    channelCheckSuccess: z.string().trim().min(1).max(200),
    channelMissing: z.string().trim().min(1).max(2_000),
    channelCheckFailed: z.string().trim().min(1).max(2_000),
    mainMenuReady: z.string().trim().min(1).max(1_000),
    profileEmpty: z.string().trim().min(1).max(2_000),
    profileItem: z.string().trim().min(1).max(2_000),
    trialAlreadyUsed: z.string().trim().min(1).max(2_000),
    trialReady: z.string().trim().min(1).max(2_000),
    trialOffer: z.string().trim().min(1).max(2_000),
    connect: z.string().trim().min(1).max(2_000),
    plans: z.string().trim().min(1).max(2_000),
    devices: z.string().trim().min(1).max(2_000),
    wallet: z.string().trim().min(1).max(3_500),
    referral: z.string().trim().min(1).max(3_500),
    support: z.string().trim().min(1).max(2_000),
    loginCode: z.string().trim().min(1).max(2_000),
    busy: z.string().trim().min(1).max(1_000),
    unknown: z.string().trim().min(1).max(1_000),
});
export const botConfigSchema = z.object({
    name: z.string().trim().min(1).max(64),
    description: z.string().trim().min(1).max(512),
    shortDescription: z.string().trim().min(1).max(120),
    startCommandDescription: z.string().trim().min(1).max(256),
    menuButtonText: z.string().trim().min(1).max(64),
    supportUrl: z.url().refine((value) => value.startsWith("https://"), "Support URL must use HTTPS"),
    channelUrl: z.union([z.literal(""), z.url().refine((value) => value.startsWith("https://t.me/") || value.startsWith("https://telegram.me/"))]),
    channelId: z.string().trim().max(64),
    referralShareText: z.string().trim().min(1).max(512),
    replyButtons: z.array(botReplyButtonSchema).min(1).max(30),
    inlineButtons: z.array(botInlineButtonSchema).max(80),
    messages: botMessagesSchema,
}).superRefine((config, ctx) => {
    const unique = (values) => new Set(values).size === values.length;
    if (!unique(config.replyButtons.map((button) => button.id))) {
        ctx.addIssue({ code: "custom", path: ["replyButtons"], message: "Reply button IDs must be unique" });
    }
    if (!unique(config.replyButtons.filter((button) => button.enabled).map((button) => button.command))) {
        ctx.addIssue({ code: "custom", path: ["replyButtons"], message: "Enabled commands must be unique" });
    }
    if (config.replyButtons.some((button) => button.enabled && button.command === "start")) {
        ctx.addIssue({ code: "custom", path: ["replyButtons"], message: "/start is reserved for Telegram deep links" });
    }
    if (!unique(config.replyButtons.filter((button) => button.enabled).map((button) => button.label))) {
        ctx.addIssue({ code: "custom", path: ["replyButtons"], message: "Enabled labels must be unique" });
    }
    if (!unique(config.inlineButtons.map((button) => button.id))) {
        ctx.addIssue({ code: "custom", path: ["inlineButtons"], message: "Inline button IDs must be unique" });
    }
});
export const commercialPolicySchema = z.object({
    referralRateBps: z.number().int().min(0).max(10_000),
    referralLifetimeDays: z.number().int().min(0).max(3_650),
    trialRequiresChannel: z.boolean(),
    walletTopupsEnabled: z.boolean(),
    minimumTopupRub: z.number().int().min(100).max(50_000),
    maximumTopupRub: z.number().int().min(100).max(100_000),
}).refine((policy) => policy.maximumTopupRub >= policy.minimumTopupRub, {
    message: "Maximum top-up must not be below minimum top-up",
    path: ["maximumTopupRub"],
});
export const abusePolicySchema = z.object({
    enabled: z.boolean(),
    blockPrivateNetworks: z.boolean(),
    blockBitTorrent: z.boolean(),
    blockOutboundSmtp25: z.boolean(),
    blockReflectionPorts: z.boolean(),
    autoSuspendCritical: z.boolean(),
    maxDeviceRebindsPerDay: z.number().int().min(1).max(100),
    evidenceRetentionDays: z.number().int().min(7).max(365),
    abuseContactEmail: z.union([z.literal(""), z.email().max(254)]),
}).superRefine((policy, ctx) => {
    if (!policy.blockPrivateNetworks) {
        ctx.addIssue({ code: "custom", path: ["blockPrivateNetworks"], message: "Private-network blocking is a protected baseline" });
    }
    if (!policy.blockBitTorrent) {
        ctx.addIssue({ code: "custom", path: ["blockBitTorrent"], message: "BitTorrent blocking is a protected baseline" });
    }
    if (!policy.blockOutboundSmtp25) {
        ctx.addIssue({ code: "custom", path: ["blockOutboundSmtp25"], message: "Outbound SMTP/25 blocking is a protected baseline" });
    }
    if (!policy.blockReflectionPorts) {
        ctx.addIssue({ code: "custom", path: ["blockReflectionPorts"], message: "UDP reflection-port blocking is a protected baseline" });
    }
});
export const networkPolicySchema = z.object({
    minimumHealthyNodes: z.number().int().min(1).max(100),
    minimumProviders: z.number().int().min(1).max(100),
    minimumAsns: z.number().int().min(1).max(100),
    maxNodeUtilizationPct: z.number().int().min(40).max(95),
    degradeLatencyMs: z.number().int().min(50).max(5_000),
    degradePacketLossPct: z.number().min(0.1).max(50),
    requireTcpFallback: z.boolean(),
    requireUdpFallback: z.boolean(),
});
export const loginSchema = z.object({
    login: z.string().trim().min(3).max(254),
    password: z.string().min(12).max(128),
});
export const registerAccountSchema = z.object({
    username: z.string().trim().regex(/^[a-zA-Z0-9_.-]{4,32}$/),
    password: z.string().min(12).max(128),
    displayName: z.string().trim().min(1).max(80),
    email: z.union([z.literal(""), z.email().max(254)]).optional(),
});
export const loginCodeSchema = z.object({
    code: z.string().regex(/^\d{6}$/),
});
export const createPlanSchema = z.object({
    slug: z.string().regex(/^[a-z0-9-]{2,32}$/),
    name: z.string().min(2).max(64),
    description: z.string().max(240).default(""),
    priceRub: z.number().int().nonnegative(),
    durationDays: z.number().int().min(1).max(730),
    maxDevices: z.number().int().min(1).max(50),
    trafficLimitBytes: z.number().int().nonnegative().default(0),
    isActive: z.boolean().default(true),
});
export const issueSubscriptionSchema = z.object({
    userId: z.uuid(),
    planId: z.uuid(),
    startsAt: z.iso.datetime().optional(),
});
export const createUserSchema = z.object({
    email: z.email().max(254).optional(),
    telegramId: z.string().regex(/^\d{1,20}$/).optional(),
    displayName: z.string().min(1).max(80),
}).refine((value) => value.email || value.telegramId, {
    message: "email or telegramId is required",
});
export const registerDeviceSchema = z.object({
    hwid: z.string().regex(/^[a-zA-Z0-9=-]{10,64}$/),
    platform: platformSchema,
    model: z.string().max(120).optional(),
    name: z.string().max(80).optional(),
});
export const walletPurchaseSchema = z.object({
    planId: z.uuid(),
    idempotencyKey: z.uuid(),
});
export const walletTopupSchema = z.object({
    amountRub: z.number().int().min(100).max(50_000),
    receiptEmail: z.email().max(254),
});
export const nodeReportSchema = z.object({
    nodeId: z.uuid(),
    status: nodeStatusSchema,
    latencyMs: z.number().int().nonnegative().max(60_000).nullable(),
    packetLossPct: z.number().min(0).max(100).nullable(),
    availableMbps: z.number().nonnegative().nullable(),
    checkedAt: z.iso.datetime(),
    capacity: z.object({
        cpuPct: z.number().min(0).max(100).nullable().optional(),
        cpuStealPct: z.number().min(0).max(100).nullable().optional(),
        memoryUsedPct: z.number().min(0).max(100).nullable().optional(),
        rxMbps: z.number().nonnegative().nullable().optional(),
        txMbps: z.number().nonnegative().nullable().optional(),
        connections: z.number().int().nonnegative().nullable().optional(),
        fileDescriptors: z.number().int().nonnegative().nullable().optional(),
        conntrack: z.number().int().nonnegative().nullable().optional(),
        headroomPct: z.number().min(0).max(100).nullable().optional(),
    }).optional(),
});
export const DEFAULT_BOT_CONFIG = {
    name: "NexoraVPN",
    description: "NexoraVPN — стабильное и защищённое подключение без сложных настроек.\n\n🎁 3 дня бесплатно новым пользователям\n⚡ Подключение одной кнопкой или по QR-коду\n🌐 100+ маршрутов подбираются автоматически\n🔁 Продление без смены ссылки\n🛟 Поддержка рядом",
    shortDescription: "NexoraVPN — надёжное подключение на всех ваших устройствах. 3 дня бесплатно.",
    startCommandDescription: "✨ Открыть главное меню NexoraVPN",
    menuButtonText: "✨ NexoraVPN",
    supportUrl: "https://t.me/NexoraVPNSupport",
    channelUrl: "",
    channelId: "",
    referralShareText: "Я пользуюсь NexoraVPN. По моей ссылке тебе уже доступны 3 дня бесплатно — попробуй на своём устройстве.",
    replyButtons: [
        { id: "connect", action: "CONNECT", label: "⚡ Подключить VPN", command: "connect", commandDescription: "Подключить NexoraVPN", row: 0, enabled: true, showInMenu: true },
        { id: "trial", action: "TRIAL", label: "🎁 3 дня бесплатно", command: "trial", commandDescription: "Получить 3 дня бесплатно", row: 0, enabled: true, showInMenu: true },
        { id: "subscription", action: "SUBSCRIPTION", label: "🛡 Моя подписка", command: "subscription", commandDescription: "Подписка и срок действия", row: 1, enabled: true, showInMenu: true },
        { id: "plans", action: "PLANS", label: "💎 Тарифы", command: "plans", commandDescription: "Тарифы и продление", row: 1, enabled: true, showInMenu: true },
        { id: "wallet", action: "WALLET", label: "💳 Оплата и баланс", command: "balance", commandDescription: "Оплата, баланс и начисления", row: 2, enabled: true, showInMenu: true },
        { id: "referral", action: "REFERRAL", label: "🤝 Пригласить друга", command: "referral", commandDescription: "Пригласить друга", row: 2, enabled: true, showInMenu: true },
        { id: "devices", action: "DEVICES", label: "📱 Устройства", command: "devices", commandDescription: "Мои устройства", row: 3, enabled: true, showInMenu: true },
        { id: "support", action: "SUPPORT", label: "🛟 Помощь", command: "support", commandDescription: "Помощь с подключением", row: 3, enabled: true, showInMenu: true },
        { id: "login", action: "LOGIN", label: "🔐 Код входа", command: "login", commandDescription: "Резервный код для браузера", row: 4, enabled: true, showInMenu: false },
    ],
    inlineButtons: [
        { id: "welcome-cabinet", context: "WELCOME", label: "✨ Открыть NexoraVPN", type: "WEB_APP", target: "trial", row: 0, enabled: true },
        { id: "channel-subscribe", context: "CHANNEL_GATE", label: "Подписаться на канал", type: "URL", target: "$channel", row: 0, enabled: true },
        { id: "channel-check", context: "CHANNEL_GATE", label: "Я подписался — проверить", type: "CALLBACK", target: "trial_channel_check", row: 1, enabled: true },
        { id: "channel-plans", context: "CHANNEL_GATE", label: "Тарифы без бонуса", type: "WEB_APP", target: "plans", row: 2, enabled: true },
        { id: "profile-connect", context: "PROFILE_ACTIVE", label: "Подключить VPN", type: "WEB_APP", target: "connect", row: 0, enabled: true },
        { id: "profile-qr", context: "PROFILE_ACTIVE", label: "QR-код", type: "WEB_APP", target: "qr", row: 1, enabled: true },
        { id: "profile-devices", context: "PROFILE_ACTIVE", label: "Устройства", type: "WEB_APP", target: "devices", row: 1, enabled: true },
        { id: "profile-trial", context: "PROFILE_EMPTY", label: "Получить 3 дня бесплатно", type: "WEB_APP", target: "trial", row: 0, enabled: true },
        { id: "trial-subscription", context: "TRIAL_ALREADY_USED", label: "Моя подписка", type: "WEB_APP", target: "connect", row: 0, enabled: true },
        { id: "trial-renew", context: "TRIAL_ALREADY_USED", label: "Продлить", type: "WEB_APP", target: "plans", row: 1, enabled: true },
        { id: "trial-ready", context: "TRIAL_READY", label: "Получить 3 дня бесплатно", type: "WEB_APP", target: "trial", row: 0, enabled: true },
        { id: "trial-activate", context: "TRIAL_OFFER", label: "Активировать 3 дня", type: "WEB_APP", target: "trial", row: 0, enabled: true },
        { id: "login-cabinet", context: "LOGIN", label: "Открыть кабинет", type: "WEB_APP", target: "", row: 0, enabled: true },
        { id: "connect-open", context: "CONNECT", label: "Подключить NexoraVPN", type: "WEB_APP", target: "connect", row: 0, enabled: true },
        { id: "plans-open", context: "PLANS", label: "Посмотреть тарифы", type: "WEB_APP", target: "plans", row: 0, enabled: true },
        { id: "devices-open", context: "DEVICES", label: "Мои устройства", type: "WEB_APP", target: "devices", row: 0, enabled: true },
        { id: "wallet-open", context: "WALLET", label: "Открыть баланс", type: "WEB_APP", target: "wallet", row: 0, enabled: true },
        { id: "wallet-topup", context: "WALLET", label: "Пополнить баланс", type: "WEB_APP", target: "wallet", row: 1, enabled: true },
        { id: "wallet-pay", context: "WALLET", label: "Оплатить тариф", type: "WEB_APP", target: "plans", row: 1, enabled: true },
        { id: "referral-share", context: "REFERRAL", label: "Отправить другу", type: "URL", target: "$referralShare", row: 0, enabled: true },
        { id: "referral-stats", context: "REFERRAL", label: "Открыть кабинет", type: "WEB_APP", target: "", row: 1, enabled: true },
        { id: "support-diagnostics", context: "SUPPORT", label: "Проверить подключение", type: "WEB_APP", target: "connect", row: 0, enabled: true },
        { id: "support-chat", context: "SUPPORT", label: "Написать в поддержку", type: "URL", target: "$support", row: 1, enabled: true },
    ],
    messages: {
        welcome: "**Добро пожаловать в NexoraVPN**\n\nПодключение, продление и устройства — прямо в Telegram.\n\n🎁 Новым пользователям доступны **3 дня бесплатно** без карты.\n🌐 100+ маршрутов подбираются автоматически.\n🔐 Отдельный пароль для WebApp не нужен.",
        referralAccepted: "🤝 **Приглашение принято**\n\nПригласивший пользователь получит награду по действующим условиям реферальной программы после подтверждённой оплаты.",
        openCabinet: "⚡ **Всё готово**\n\nКабинет откроется сразу под вашим Telegram-аккаунтом — без логина и пароля.",
        channelNotConfigured: "Бесплатные 3 дня пока не активированы владельцем канала. Платные тарифы и поддержка доступны без подписки на канал.",
        channelUnavailable: "Telegram сейчас не дал проверить подписку. Убедитесь, что вы подписаны на русскоязычный канал NexoraVPN, и нажмите проверку ещё раз.",
        channelRequired: "Подпишитесь на канал NexoraVPN и получите 3 дня бесплатно. После подписки нажмите «Проверить подписку ✅». Можно отказаться от пробного периода и сразу открыть меню.",
        channelConfirmed: "Готово — Telegram подтвердил подписку на канал. Теперь можно активировать {trialDays} бесплатных дня.",
        channelCheckSuccess: "Подписка подтверждена",
        channelMissing: "Подписка пока не найдена. Подпишитесь на канал и повторите проверку.",
        channelCheckFailed: "Не удалось проверить подписку. Проверьте ещё раз через минуту.",
        mainMenuReady: "✨ **Главное меню NexoraVPN открыто**",
        profileEmpty: "🛡 **Подписки пока нет**\n\nПолучите 3 дня бесплатно или выберите тариф.",
        profileItem: "🛡 **{planName}**\n\nСтатус: **{status}**\nДействует до: **{expiresAt}**\nУстройства: **{activeDevices} из {maxDevices}**\n{devices}",
        trialAlreadyUsed: "Пробный период доступен один раз только новому пользователю. Откройте свою подписку или продлите её в кабинете.",
        trialReady: "Подписка на канал подтверждена. Бесплатный доступ готов к активации.",
        trialOffer: "{trialDays} дня полного доступа бесплатно: без карты, автосписаний и скрытых условий. Предложение действует один раз для нового пользователя.",
        connect: "⚡ **Подключение NexoraVPN**\n\nОткройте персональную кнопку ниже. В кабинете будут готовая ссылка и QR-код.",
        plans: "💎 **Тарифы NexoraVPN**\n\nДоступны периоды 1, 3, 6 и 12 месяцев для 2, 5, 10 или 30 устройств. Продление сохраняет текущую ссылку подключения.",
        devices: "📱 **Ваши устройства**",
        wallet: "💳 **Оплата и баланс**\n\nБаланс: **{balance}**\nПартнёрские начисления: **{referralNet}**\nДоступно к выводу: **{withdrawable}**\nВ заявках: **{pendingWithdrawal}**\n\nВ кабинете можно пополнить баланс или оформить вывод партнёрских начислений по СБП.",
        referral: "🤝 **Приглашайте друзей**\n\nВаша ссылка:\n{referralLink}\n\nПриглашено: {invitedCount}\nОплатили: {payingInvitedCount}\nСтавка: **{referralPercent}%**\nСрок начислений: **{referralLifetime}**\nПодтверждённая награда: **{referralNet}**\n\nДруг получает 3 дня бесплатно. Бонус за реферальную ссылку и бонус за подписку на канал не суммируются — максимум 3 бесплатных дня.",
        support: "🛟 **Поддержка NexoraVPN**\n\nЕсли что-то не открывается, сначала нажмите «Проверить подключение». Nexora обновит состояние подписки и покажет одно конкретное действие, если оно действительно нужно.",
        loginCode: "🔐 **Резервный код входа: {loginCode}**\n\nОн действует 10 минут и сработает только один раз.",
        busy: "Сервис временно занят. Попробуйте ещё раз через минуту.",
        unknown: "Не понял команду. Используйте кнопки главного меню NexoraVPN.",
    },
};
export const DEFAULT_COMMERCIAL_POLICY = {
    referralRateBps: 1_000,
    referralLifetimeDays: 0,
    trialRequiresChannel: true,
    walletTopupsEnabled: true,
    minimumTopupRub: 100,
    maximumTopupRub: 50_000,
};
export const DEFAULT_ABUSE_POLICY = {
    enabled: true,
    blockPrivateNetworks: true,
    blockBitTorrent: true,
    blockOutboundSmtp25: true,
    blockReflectionPorts: true,
    autoSuspendCritical: true,
    maxDeviceRebindsPerDay: 5,
    evidenceRetentionDays: 90,
    abuseContactEmail: "",
};
export const DEFAULT_NETWORK_POLICY = {
    minimumHealthyNodes: 3,
    minimumProviders: 3,
    minimumAsns: 3,
    maxNodeUtilizationPct: 80,
    degradeLatencyMs: 250,
    degradePacketLossPct: 5,
    requireTcpFallback: true,
    requireUdpFallback: true,
};
export * from "./campaign-templates.js";
//# sourceMappingURL=index.js.map